import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  Trophy,
  Headset,
  Compass,
  ShieldCheck,
  HeartHandshake,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import CourtConnectPartnerForm from "@/components/CourtConnectPartnerForm";
import analytics from "@/utils/analytics";
import { usePartnerViewed } from "@/utils/trackingHooks";

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";
const PATH = "/partner-with-us";
const LOGO_URL = "https://cdn.candopayments.com/logos/cando_long_colour_web_transparent.svg";

// Hero background: padel racket and ball on court (1920x800 landing asset,
// with a 2x retina variant), served from /public/images. Decorative — the
// headline carries the content. The artwork is the plain (undarkened)
// export; all darkening happens in the CSS scrim so it can be tuned per
// breakpoint and the racket/ball side stays vibrant.
const HERO_IMAGE = "/images/cando-court-connect-padel-landing-header-plain-tall-1920x800.jpg";
const HERO_IMAGE_2X = "/images/cando-court-connect-padel-landing-header-plain-tall-3840x1600.jpg";
const OG_IMAGE = `${PROD_URL}/images/cando-court-connect-padel-og.jpg`;

// Approved copy 2026-09-11 (content owner: Gary Cameron) — title and meta
// replaced exactly as specified; canonical /partner-with-us retained.
const META_TITLE = "Referral Partner Programme for Developers";
const META_DESCRIPTION =
  "Developers, agencies and platform providers: register interest in referring clients to CanDo Payments and learn how the partner programme works.";

// Single source for the visible accordion AND the FAQPage schema (via
// SEOHead's `faqs` prop) so they can never drift.
const FAQS = [
  {
    q: "Do I need to become a payments expert?",
    a: "No. Make the introduction and give us the context you have. CanDo handles the payment discussion, application and onboarding with the client.",
  },
  {
    q: "What happens after I register my interest?",
    a: "A member of the CanDo team will contact you to understand your work, the clients you support and whether the partner programme is a suitable fit. Registering interest does not automatically confirm partner status.",
  },
  {
    q: "How do partner rewards work?",
    a: "Rewards apply to successful introductions under terms agreed with CanDo before referrals begin. We'll explain the structure during the partner conversation.",
  },
  {
    q: "Can I refer a client who already uses Stripe or another provider?",
    a: "Yes, if the client wants to review its current setup or consider another option. We'll assess the business and explain whether CanDo is a suitable fit; an introduction does not assume that a provider change is right.",
  },
  {
    q: "Who is the programme for?",
    a: "The page is aimed at developers, web agencies, platform providers, consultants and other businesses whose clients ask for help with payments.",
  },
];

const REFERRAL_SITUATIONS = [
  "needs help choosing an online checkout or payment gateway",
  "wants online and in-person payments to work together",
  "is reviewing an existing provider, including Stripe",
  "wants more personal support with their payment setup",
];

const HOW_REFERRALS_WORK = [
  ["Tell us about the opportunity.", "Introduce your client in the way that suits you both."],
  [
    "We assess what they need.",
    "CanDo speaks with the client, reviews their payment setup and explains the suitable options.",
  ],
  [
    "We manage the payments journey.",
    "Your team stays focused on its own work. If the introduction becomes a successful CanDo customer, you can earn ongoing rewards under the partner terms agreed with us.",
  ],
];

// ─── small presentational helpers (site landing-page language) ───────────
function SectionHeading({ eyebrow, title, children, center = true, className = "" }) {
  return (
    <div className={`${center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} mb-8 ${className}`}>
      {eyebrow && (
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-dark mb-3">{eyebrow}</p>
      )}
      <h2 className="font-heading text-2xl sm:text-4xl font-bold text-brand-black tracking-tight">{title}</h2>
      {children && <p className="text-base sm:text-lg text-brand-grey leading-relaxed mt-4">{children}</p>}
    </div>
  );
}

// In-page anchor CTA that fires an analytics event before the native jump.
function AnchorCTA({ href, event, variant = "primary", className = "", children, testId }) {
  const base =
    variant === "primary"
      ? "cta-primary text-white"
      : "bg-white border-2 border-brand-aqua text-brand-aqua-dark hover:bg-brand-aqua-light";
  return (
    <a
      href={href}
      onClick={() => event && analytics.landingEvent(event)}
      className={`inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm transition-all ${base} ${className}`}
      data-testid={testId}
      data-measurement-owned={event ? "landing" : undefined}
    >
      {children}
    </a>
  );
}

// Minimal padel-ball glyph (dotted ball) for the event sections.
function PadelBall({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="9" cy="9" r="1.2" fill="currentColor" />
      <circle cx="15" cy="9" r="1.2" fill="currentColor" />
      <circle cx="9" cy="15" r="1.2" fill="currentColor" />
      <circle cx="15" cy="15" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}

export default function PartnerWithUsPage() {
  usePartnerViewed("court_connect_partner_landing", { itemType: "event_landing" });

  const formRef = useRef(null);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [formOnScreen, setFormOnScreen] = useState(false);

  // Sticky mobile CTA: appears past the hero, hidden while the form is on
  // screen so it never covers the controls (same pattern as
  // PartnerLandingPage).
  useEffect(() => {
    const el = formRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setFormOnScreen(e.isIntersecting)),
      { threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolledPastHero(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showSticky = scrolledPastHero && !formOnScreen;

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title={META_TITLE}
        description={META_DESCRIPTION}
        path={PATH}
        robots="index, follow"
        suppressBreadcrumb
        faqs={FAQS}
        ogImage={OG_IMAGE}
        ogImageAlt="CanDo Payments — proud sponsor of Court Connect Padel, 17 September 2026, Carbon Padel, Manchester"
      />

      {/* ── 1. Event strip ─────────────────────────────────────────── */}
      <div
        className="bg-gradient-to-r from-brand-aqua-dark via-brand-aqua to-brand-pink text-white"
        data-testid="cc-announcement-bar"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs sm:text-sm font-semibold tracking-wide text-center">
          <Trophy size={15} className="flex-shrink-0" aria-hidden="true" />
          <span>Proud sponsor of Court Connect Padel</span>
          <span className="text-white/85 font-medium">&middot; 17 September 2026 &middot; Carbon Padel, Manchester</span>
        </div>
      </div>

      {/* ── Minimal event-landing header ───────────────────────────── */}
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm border-b border-brand-light-grey/60"
        data-testid="cc-landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" aria-label="CanDo Payments home">
            <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-6 sm:h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a
              href="tel:+448003682989"
              onClick={() => analytics.clickPhone()}
              className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-brand-aqua-dark hover:text-brand-aqua transition-colors"
              data-testid="cc-header-phone"
              data-measurement-owned="contact"
            >
              <Phone size={15} /> 0800 368 2989
            </a>
            <AnchorCTA href="#partner-form" event="cc_partner_cta_click" className="!px-5 !py-2.5" testId="cc-header-cta">
              Register your interest
            </AnchorCTA>
          </div>
        </div>
      </header>

      <main>
        {/* ── 2. Developer referral hero ───────────────────────────── */}
        <section className="relative overflow-hidden bg-slate-950">
          <img
            src={HERO_IMAGE}
            srcSet={`${HERO_IMAGE} 1920w, ${HERO_IMAGE_2X} 3840w`}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            width="1920"
            height="800"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover object-[62%_center]"
          />
          {/* legibility scrim — all the darkening lives here (the artwork is
              the plain export). On phones (below lg) the scrim is uniform so
              full-width text stays legible; from lg up it grades
              left-to-right, fading out over the racket and ball so that side
              stays vibrant */}
          <div
            className="absolute inset-0 bg-slate-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-slate-950/90 lg:via-slate-950/60 lg:via-45% lg:to-slate-950/10"
            aria-hidden="true"
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-4">
                For developers, agencies and platform providers
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
                Refer clients to a payments team <span className="gradient-text-brand">you can trust</span>
              </h1>
              <p className="text-base sm:text-lg text-white/85 leading-relaxed mt-5">
                When a client needs help with online, in-person or phone payments, introduce them to CanDo.
                We&rsquo;ll take care of the payments conversation while you stay focused on the work you do best.
                Successful introductions can earn ongoing rewards, subject to agreed partner terms.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <AnchorCTA href="#partner-form" event="cc_partner_cta_click" testId="cc-hero-primary">
                  Register your interest as a referral partner <ArrowRight size={16} />
                </AnchorCTA>
                <a
                  href="#on-court"
                  onClick={() => analytics.landingEvent("cc_meet_team_click")}
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/80 text-white font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white/10 transition-all"
                  data-testid="cc-hero-secondary"
                  data-measurement-owned="landing"
                >
                  Meet us at Court Connect
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. Referral situations ───────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="A useful introduction" title="When could you bring CanDo into the conversation?">
              A referral may be worth discussing when your client:
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {REFERRAL_SITUATIONS.map((situation) => (
                <div
                  key={situation}
                  className="flex items-start gap-3 bg-brand-off-white border border-brand-light-grey rounded-2xl px-5 py-4"
                >
                  <span className="w-6 h-6 rounded-full bg-brand-aqua-light flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-brand-aqua-dark" />
                  </span>
                  <span className="text-sm text-brand-black leading-relaxed">{situation}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-brand-grey leading-relaxed mt-6">
              Not sure whether the opportunity is a fit? Introduce us and we&rsquo;ll give you a straightforward answer.
            </p>
            <div className="text-center mt-6">
              <AnchorCTA href="#partner-form" event="cc_partner_cta_click" testId="cc-situations-cta">
                Register your partner interest <ArrowRight size={16} />
              </AnchorCTA>
            </div>
          </div>
        </section>

        {/* ── 4. How referrals work ────────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-brand-off-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading
              eyebrow="How referrals work"
              title="Make the introduction. We'll handle the payments conversation."
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {HOW_REFERRALS_WORK.map(([title, body], i) => (
                <div
                  key={title}
                  className="bg-white border border-brand-light-grey rounded-2xl p-5 shadow-sm h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  data-testid={`cc-step-${i + 1}`}
                >
                  <div className="w-9 h-9 rounded-full bg-brand-aqua text-white font-heading font-bold flex items-center justify-center mb-3">
                    {i + 1}
                  </div>
                  <h3 className="font-heading text-base font-bold text-brand-black mb-1.5">{title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <AnchorCTA href="#partner-form" event="cc_partner_cta_click" testId="cc-steps-cta">
                Register your interest as a referral partner <ArrowRight size={16} />
              </AnchorCTA>
            </div>
          </div>
        </section>

        {/* ── 5. Partner interest form (≈35–45% of page depth) ─────── */}
        <section ref={formRef} id="partner-form" className="py-12 sm:py-16 bg-white scroll-mt-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Let's talk" title="Interested in referring clients to CanDo?">
              Tell us who you are and the kind of work you do. A member of the CanDo team will contact you to understand
              the clients you support and explain how referrals and partner terms work. Registering interest does not
              automatically confirm partner status.
            </SectionHeading>
            <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm p-6 sm:p-8">
              <CourtConnectPartnerForm />
            </div>
          </div>
        </section>

        {/* ── 6. Referral reassurance ──────────────────────────────── */}
        <section className="py-12 sm:py-16 bg-brand-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading
              eyebrow="Your client relationship matters"
              title="A payments partner you can feel comfortable introducing"
            >
              Your reputation comes with every introduction. CanDo takes responsibility for the payments conversation
              while respecting the relationship you have built with your client.
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                [
                  Headset,
                  "People your client can speak to.",
                  "UK-based support from a team that understands the account.",
                ],
                [
                  Compass,
                  "Advice shaped around the business.",
                  "We review how the client takes payments before recommending an option.",
                ],
                [
                  HeartHandshake,
                  "A clear division of responsibility.",
                  "You remain the technology partner; CanDo handles the payment discussion and onboarding.",
                ],
              ].map(([Icon, title, body]) => (
                <div key={title} className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm h-full">
                  <div className="w-10 h-10 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-3">
                    <Icon size={19} className="text-brand-aqua-dark" />
                  </div>
                  <h3 className="font-heading text-base font-bold text-brand-black mb-1.5">{title}</h3>
                  <p className="text-sm text-brand-grey leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
            <p
              className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm font-semibold text-brand-black mt-8"
              data-testid="cc-trust-row"
            >
              <ShieldCheck size={15} className="text-brand-aqua-dark flex-shrink-0" aria-hidden="true" />
              <span>Certified B Corporation&trade;</span>
              <span className="text-brand-grey" aria-hidden="true">
                &middot;
              </span>
              <span>UK-based support</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link
                to="/reviews"
                onClick={() => analytics.landingEvent("cc_reviews_click")}
                className="cta-primary text-white inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm transition-all"
                data-testid="cc-reviews-cta"
                data-measurement-owned="landing"
              >
                Read customer reviews <ArrowRight size={16} />
              </Link>
              <Link
                to="/partner-programme"
                onClick={() => analytics.landingEvent("cc_programme_click")}
                className="bg-white border-2 border-brand-aqua text-brand-aqua-dark hover:bg-brand-aqua-light inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm transition-all"
                data-testid="cc-programme-cta"
                data-measurement-owned="landing"
              >
                Explore the full partner programme
              </Link>
            </div>
          </div>
        </section>

        {/* ── 7. Event meeting point ───────────────────────────────── */}
        <section id="on-court" className="py-10 sm:py-14 bg-white scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-brand-aqua-dark text-white p-7 sm:p-10">
              <div className="absolute inset-0 pointer-events-none opacity-[0.08]" aria-hidden="true">
                <div className="absolute inset-5 border-2 border-white rounded-xl" />
                <div className="absolute left-5 right-5 top-1/2 border-t-2 border-white" />
                <div className="absolute top-5 bottom-5 left-1/2 border-l-2 border-white" />
              </div>
              <div className="relative max-w-2xl">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-light mb-3">
                  <PadelBall size={16} /> We&rsquo;re on court too
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                  Meet the CanDo team at Court Connect Padel
                </h2>
                <p className="text-white/85 leading-relaxed mt-4">
                  If you are at the event, come over and say hello. Tell us about the websites, platforms or businesses
                  you support, and we can talk through the kinds of referral that may be a good fit.
                </p>
                <a
                  href="#partner-form"
                  onClick={() => analytics.landingEvent("cc_partner_cta_click")}
                  className="inline-flex items-center justify-center gap-2 bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-3 text-sm hover:bg-brand-off-white transition-colors mt-6"
                  data-testid="cc-oncourt-cta"
                  data-measurement-owned="landing"
                >
                  Find the partner form <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. FAQ + closing referral-interest CTA ───────────────── */}
        <section className="py-12 sm:py-16 bg-brand-off-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Frequently asked questions" title="Questions about becoming a referral partner" />
            <ResponsiveAccordion items={FAQS} testIdPrefix="cc-faq" headingLevel="h3" />
          </div>
        </section>

        <section className="cta-section-bg py-14 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Refer clients to a payments team you can trust
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
              <a
                href="#partner-form"
                onClick={() => analytics.landingEvent("cc_partner_cta_click")}
                className="inline-flex items-center justify-center gap-2 bg-white text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-brand-off-white transition-colors"
                data-testid="cc-final-primary"
                data-measurement-owned="landing"
              >
                Register your interest as a referral partner <ArrowRight size={16} />
              </a>
              <Link
                to="/partner-programme"
                onClick={() => analytics.landingEvent("cc_programme_click")}
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-semibold rounded-full px-6 py-3.5 text-sm hover:bg-white/10 transition-colors"
                data-testid="cc-final-secondary"
                data-measurement-owned="landing"
              >
                Explore the partner programme
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ── Minimal footer ──────────────────────────────────────────── */}
      <footer className="bg-white border-t border-brand-light-grey py-10" data-testid="cc-landing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-6 w-auto" />
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm" aria-label="Legal">
            <Link to="/partner-programme" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Partner Programme
            </Link>
            <Link to="/reviews" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Reviews
            </Link>
            <Link to="/privacy-policy" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Terms
            </Link>
            <Link to="/contact" className="text-brand-grey hover:text-brand-aqua transition-colors">
              Contact
            </Link>
          </nav>
        </div>
        {/* Single-expression child: adjacent text + {year} children merge
            into one DOM text node in the react-snap snapshot and break
            hydration (React #418). */}
        <p className="text-center text-xs text-brand-grey mt-6 px-4">
          {`\u00a9 ${new Date().getFullYear()} Can Do Payments Ltd. Company No. 11701057. VAT: GB311835329`}
        </p>
      </footer>

      {/* Sticky mobile CTA — appears after the hero, hidden while the form is
          on screen so it never covers the form fields or cookie controls. */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 p-3 bg-white/95 backdrop-blur-md border-t border-brand-light-grey transition-transform duration-300 ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showSticky}
        data-testid="cc-sticky-cta"
      >
        <a
          href="#partner-form"
          onClick={() => analytics.landingEvent("cc_mobile_sticky_cta_click")}
          tabIndex={showSticky ? 0 : -1}
          className="cta-primary text-white font-semibold rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2 w-full"
          data-testid="cc-sticky-cta-link"
          data-measurement-owned="landing"
        >
          Register your interest as a referral partner <ArrowRight size={16} />
        </a>
      </div>
    </div>
  );
}
